<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  type PrimitiveProps,
  useEmitAsProps,
} from 'radix-vue'
import { X } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

export interface DismissableLayerProps extends PrimitiveProps {
  /**
   * When `true`, hover/focus/click interactions will be disabled on elements outside
   * the `DismissableLayer`. Users will need to click twice on outside elements to
   * interact with them: once to close the `DismissableLayer`, and again to trigger the element.
   */
  disableOutsidePointerEvents?: boolean
}

export interface DialogContentImplProps extends DismissableLayerProps {
  /**
   * Used to force mounting when more control is needed. Useful when
   * controlling transntion with Vue native transition or other animation libraries.
   */
  forceMount?: boolean
  /**
   * When `true`, focus cannot escape the `Content` via keyboard,
   * pointer, or a programmatic focus.
   * @defaultValue false
   */
  trapFocus?: boolean
}

export interface DialogContentProps extends DialogContentImplProps {
  /**
   * Used to force mounting when more control is needed. Useful when
   * controlling animation with Vue animation libraries.
   */
  forceMount?: boolean
}

export type PointerDownOutsideEvent = CustomEvent<{
  originalEvent: PointerEvent
}>
export type FocusOutsideEvent = CustomEvent<{
  originalEvent: FocusEvent
}>

// eslint-disable-next-line ts/consistent-type-definitions
type DialogEmits = {
  /**
   * Event handler called when the escape key is down.
   * Can be prevented.
   */
  'escapeKeyDown': [event: KeyboardEvent]
  /**
   * Event handler called when the a `pointerdown` event happens outside of the `DismissableLayer`.
   * Can be prevented.
   */
  'pointerDownOutside': [event: PointerDownOutsideEvent]
  /**
   * Event handler called when the focus moves outside of the `DismissableLayer`.
   * Can be prevented.
   */
  'focusOutside': [event: FocusOutsideEvent]
  /**
   * Event handler called when an interaction happens outside the `DismissableLayer`.
   * Specifically, when a `pointerdown` event happens outside or focus moves outside of it.
   * Can be prevented.
   */
  'interactOutside': [event: PointerDownOutsideEvent | FocusOutsideEvent]
  /**
   * Handler called when the `DismissableLayer` should be dismissed
   */
  'dismiss': []
  /**
   * Event handler called when auto-focusing on open.
   * Can be prevented.
   */
  'openAutoFocus': [event: Event]
  /**
   * Event handler called when auto-focusing on close.
   * Can be prevented.
   */
  'closeAutoFocus': [event: Event]
}

const props = defineProps<DialogContentProps & { class?: string }>()
const emits = defineEmits<DialogEmits>()

const emitsAsProps = useEmitAsProps(emits)

// Add event handlers to prevent closing on outside clicks
function handlePointerDownOutside(event: Event) {
  event.stopPropagation()
  event.preventDefault()
}

function handleFocusOutside(event: Event) {
  event.stopPropagation()
  event.preventDefault()
}

function handleInteractOutside(event: Event) {
  event.stopPropagation()
  event.preventDefault()
}
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 grid place-items-end overflow-y-auto bg-[radial-gradient(circle_at_50%_100%,rgba(0,0,0,0.5)_40%,rgba(0,0,0,0.7))] p-3 backdrop-blur sm:place-items-center sm:bg-[radial-gradient(rgba(0,0,0,0.5)_40%,rgba(0,0,0,0.7))] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0"
      style="opacity: 1; --visual-viewport-height: 924px; --black-05: black"
    />
    <DialogContent
      :class="
        cn(
          'fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg md:w-full',
          props.class,
        )
      " v-bind="{ ...props, ...emitsAsProps }"
      @pointer-down-outside="handlePointerDownOutside"
      @focus-outside="handleFocusOutside"
      @interact-outside="handleInteractOutside"
    >
      <slot />
      <DialogClose
        class="text-primary hover:bg-primary:20 focus:shadow-secondary absolute top-[10px] right-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
        aria-label="Close"
      >
        <span class="sr-only">Close</span>
        <X class="w-4 h-4 text-primary" />
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
